/**
 * Format a decimal value as a percentage with optional precision.
 * Removes trailing zeros to match d3-format behavior.
 *
 * @param value - The decimal value to format (e.g., 0.98 for 98%)
 * @param precision - Number of decimal places (default: 1)
 * @returns Formatted percentage string (e.g., "98%" or "<0.1%")
 *
 * @example
 * formatPercent(0.98, 1) // "98%"
 * formatPercent(0.981, 1) // "98.1%"
 * formatPercent(0.0005, 1) // "<0.1%"
 * formatPercent(0.00005, 2) // "<0.01%"
 */
export function formatPercent(value: number, precision = 1): string {
  const percent = value * 100;
  const threshold = Math.pow(10, -precision);

  // Handle values that round to 0 but aren't actually 0
  if (percent > 0 && percent < threshold) {
    // Format the threshold without trailing zeros
    const formattedThreshold = Number(threshold.toFixed(precision)).toString();
    return `<${formattedThreshold}%`;
  }

  // Format and remove trailing zeros
  const formatted = percent.toFixed(precision);
  const withoutTrailingZeros = Number(formatted).toString();
  return `${withoutTrailingZeros}%`;
}

/**
 * Format a number with thousands separators.
 *
 * @param value - The number to format
 * @returns Formatted number string with commas (e.g., "1,234,567")
 *
 * @example
 * formatNumber(1234567) // "1,234,567"
 * formatNumber(1000) // "1,000"
 * formatNumber(0) // "0"
 */
export function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US').format(value);
}
